import { FunctionComponent, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { Checkbox } from "@/components/Checkbox/Checkbox";

import { CreateCaseStudy, CreateCaseStudySchema } from "../schema/caseStudies";

interface AddCaseStudyModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCreate: (data: CreateCaseStudy) => void;
}

const defaultValues: CreateCaseStudy = {
	clientName: "",
	website: "",
	goalsChallenge: "",
	solution: "",
	outcome: "",
	quoteText: "",
	quoteAttribution: "",
	autoEnrich: true,
};

export const AddCaseStudyModal: FunctionComponent<AddCaseStudyModalProps> = ({
	isOpen,
	onClose,
	onCreate,
}) => {
	const {
		register,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors },
	} = useForm<CreateCaseStudy>({
		defaultValues,
		resolver: zodResolver(CreateCaseStudySchema),
	});

	useEffect(() => {
		if (isOpen) {
			reset(defaultValues);
		}
	}, [isOpen, reset]);

	const watchedValues = watch();

	const onSubmit = (data: CreateCaseStudy) => {
		onCreate(data);
		onClose();
	};

	return (
		<Modal
			handleClose={onClose}
			handleSave={handleSubmit(onSubmit)}
			isOpened={isOpen}
			size="lg"
			submitButtonText="Create"
			title="Add Case Study"
		>
			<div className="flex gap-6 max-h-[60vh]">
				{/* Left column – Form */}
				<div className="flex-1 space-y-4 overflow-y-auto pr-2">
					{/* Client Name */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
						<input
							className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
							placeholder="e.g. Acme Corp"
							type="text"
							{...register("clientName")}
						/>
						{errors.clientName && (
							<p className="mt-1 text-xs text-red-500">{errors.clientName.message}</p>
						)}
					</div>

					{/* Website */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Website (optional)
						</label>
						<input
							className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
							placeholder="https://example.com"
							type="text"
							{...register("website")}
						/>
					</div>

					{/* Goals */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Goals / Challenges *
						</label>
						<textarea
							className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y min-h-[60px] focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
							placeholder="What was the client trying to achieve?"
							{...register("goalsChallenge")}
						/>
						{errors.goalsChallenge && (
							<p className="mt-1 text-xs text-red-500">{errors.goalsChallenge.message}</p>
						)}
					</div>

					{/* Solution */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Solution / What We Did *
						</label>
						<textarea
							className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y min-h-[60px] focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
							placeholder="How did you solve the problem?"
							{...register("solution")}
						/>
						{errors.solution && (
							<p className="mt-1 text-xs text-red-500">{errors.solution.message}</p>
						)}
					</div>

					{/* Outcome */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Outcome / Impact *
						</label>
						<textarea
							className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y min-h-[60px] focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
							placeholder="What were the measurable results?"
							{...register("outcome")}
						/>
						{errors.outcome && (
							<p className="mt-1 text-xs text-red-500">{errors.outcome.message}</p>
						)}
					</div>

					{/* Quote Text */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">Quote (optional)</label>
						<textarea
							className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y min-h-[60px] focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
							placeholder="Client testimonial quote..."
							{...register("quoteText")}
						/>
					</div>

					{/* Quote Attribution */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Quote Attribution (optional)
						</label>
						<input
							className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
							placeholder="e.g. John Doe, VP of Engineering, Acme Corp"
							type="text"
							{...register("quoteAttribution")}
						/>
					</div>

					{/* Auto-enrich checkbox */}
					<div className="pt-2 border-t border-gray-200">
						<Checkbox
							isChecked={watchedValues.autoEnrich ?? true}
							name="autoEnrich"
							onChange={() => setValue("autoEnrich", !watchedValues.autoEnrich)}
						>
							Auto-enrich with LinkedIn data (company description, industry, employee count)
						</Checkbox>
					</div>
				</div>

				{/* Right column – Preview */}
				<div className="flex-1 overflow-y-auto">
					<div className="rounded-lg bg-brand-white border border-gray-200 p-4 h-full">
						<p className="text-xs font-semibold text-text-meta uppercase tracking-wider mb-3">
							Preview
						</p>

						<div className="space-y-3">
							<div>
								<p className="text-xs font-medium text-text-meta">Client Name</p>
								<p className="text-sm text-gray-900">
									{watchedValues.clientName || (
										<span className="text-gray-400 italic">Not entered</span>
									)}
								</p>
							</div>

							<div>
								<p className="text-xs font-medium text-text-meta">Website</p>
								<p className="text-sm text-gray-900">
									{watchedValues.website || (
										<span className="text-gray-400 italic">Not entered</span>
									)}
								</p>
							</div>

							<div>
								<p className="text-xs font-medium text-text-meta">Goals / Challenges</p>
								<p className="text-sm text-gray-900 whitespace-pre-line">
									{watchedValues.goalsChallenge || (
										<span className="text-gray-400 italic">Not entered</span>
									)}
								</p>
							</div>

							<div>
								<p className="text-xs font-medium text-text-meta">Solution</p>
								<p className="text-sm text-gray-900 whitespace-pre-line">
									{watchedValues.solution || (
										<span className="text-gray-400 italic">Not entered</span>
									)}
								</p>
							</div>

							<div>
								<p className="text-xs font-medium text-text-meta">Outcome</p>
								<p className="text-sm text-gray-900 whitespace-pre-line">
									{watchedValues.outcome || (
										<span className="text-gray-400 italic">Not entered</span>
									)}
								</p>
							</div>

							{(watchedValues.quoteText || watchedValues.quoteAttribution) && (
								<div>
									<p className="text-xs font-medium text-text-meta">Quote</p>
									{watchedValues.quoteText && (
										<p className="text-sm text-gray-900 italic">
											&ldquo;{watchedValues.quoteText}&rdquo;
										</p>
									)}
									{watchedValues.quoteAttribution && (
										<p className="text-xs text-gray-600 mt-0.5">
											— {watchedValues.quoteAttribution}
										</p>
									)}
								</div>
							)}

							<div className="pt-2 border-t border-gray-200">
								<p className="text-xs font-medium text-text-meta">LinkedIn Enrichment</p>
								<p className="text-sm text-gray-900">
									{watchedValues.autoEnrich ? "Enabled" : "Disabled"}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};
